import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { fmt } from "@/data/catalog";
import { useCart } from "@/context/CartContext";
import { track } from "@/components/common/Analytics";
import confetti from "canvas-confetti";
import { trackEvent } from "@/lib/track";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id") || params.get("payment_intent");
  const provider = params.get("provider");
  const [state, setState] = useState("polling"); // polling | paid | failed | timeout
  const [order, setOrder] = useState(null);
  const { clear } = useCart();
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let attempts = 0;
    const finalizePaid = (data, amount) => {
      setState("paid");
      if (!cleared.current) {
        clear();
        cleared.current = true;
        track("Purchase", {
          value: amount, currency: (data?.currency || "aud").toUpperCase(),
          content_ids: ["single"], content_type: "product",
        }, sessionId);
        trackEvent("purchase", { value: amount });
      }
      api.get(`/orders/by-session/${sessionId}`).then((r) => setOrder(r.data)).catch(() => {});
    };
    const poll = async () => {
      try {
        if (provider === "paypal") {
          const { data } = await api.get(`/orders/by-session/${sessionId}`);
          if (data.payment_status === "paid") { finalizePaid(data, data.total); return; }
        } else if (provider === "stripe") {
          const { data } = await api.get(`/stripe/pi-status/${sessionId}`);
          if (data.payment_status === "paid") { finalizePaid(data, data.amount); return; }
        } else {
          const { data } = await api.get(`/checkout/status/${sessionId}`);
          if (data.payment_status === "paid") {
            finalizePaid(data, data.amount_total ? data.amount_total / 100 : undefined);
            return;
          }
          if (data.status === "expired") { setState("failed"); return; }
        }
      } catch { /* keep trying */ }
      attempts += 1;
      if (attempts >= 20) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
  }, [sessionId, provider, clear]);

  useEffect(() => {
    if (state !== "paid") return;
    const colors = ["#FBC4A8", "#F6B393", "#FBC4A8", "#FFFCFA", "#FFFCFA"];
    confetti({ particleCount: 130, spread: 75, origin: { y: 0.32 }, colors, disableForReducedMotion: true });
    const t1 = setTimeout(() => confetti({ particleCount: 70, angle: 60, spread: 70, origin: { x: 0, y: 0.5 }, colors }), 220);
    const t2 = setTimeout(() => confetti({ particleCount: 70, angle: 120, spread: 70, origin: { x: 1, y: 0.5 }, colors }), 320);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [state]);

  return (
    <div data-testid="checkout-success-page" className="mx-auto flex min-h-[70vh] max-w-2xl flex-col items-center justify-center px-6 py-20 text-center">
      {state === "polling" && (
        <>
          <Loader2 className="h-12 w-12 animate-spin text-[#FBC4A8]" />
          <h1 className="mt-6 font-display text-4xl font-light text-[#2C2B2C]">Confirming your payment…</h1>
          <p className="mt-3 text-[#8A7F86]">One moment while we verify your order.</p>
        </>
      )}

      {state === "paid" && (
        <>
          <CheckCircle2 className="h-16 w-16 text-[#4A5D4E]" />
          <p className="mt-6 text-[0.7rem] uppercase tracking-[0.28em] text-[#FBC4A8]">Order Confirmed</p>
          <h1 className="mt-3 font-display text-5xl font-light text-[#2C2B2C]">Welcome to your glow era.</h1>
          <p className="mt-4 max-w-md text-[#8A7F86]">
            Thank you for your order. A confirmation is on its way to your inbox, and your parcel will ship from our Gold Coast studio within 1 business day.
          </p>
          {order && (
            <div data-testid="order-summary" className="mt-8 w-full rounded-[2rem] border border-[#F2E8E6] bg-white p-6 text-left">
              <p className="text-xs uppercase tracking-[0.15em] text-[#8A7F86]">Order #{order.id.slice(0, 8).toUpperCase()}</p>
              <div className="mt-4 space-y-2">
                {order.items.map((it) => (
                  <div key={it.sku} className="flex justify-between text-sm">
                    <span className="text-[#8A7F86]">{it.name} × {it.quantity}</span>
                    <span className="text-[#2C2B2C]">{fmt(it.line_total)}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex justify-between border-t border-[#F2E8E6] pt-4">
                <span className="text-sm uppercase tracking-[0.12em] text-[#8A7F86]">Total Paid</span>
                <span className="font-display text-2xl text-[#2C2B2C]">{fmt(order.total)}</span>
              </div>
            </div>
          )}
          <Link to="/" data-testid="success-continue" className="mt-8 bg-[#232222] px-10 rounded-full py-4 text-xs uppercase tracking-[0.18em] text-[#FFFCFA]">Continue Shopping</Link>
        </>
      )}

      {(state === "failed" || state === "timeout") && (
        <>
          <XCircle className="h-16 w-16 text-[#b9433a]" />
          <h1 className="mt-6 font-display text-4xl font-light text-[#2C2B2C]">
            {state === "timeout" ? "Still processing…" : "Payment not completed"}
          </h1>
          <p className="mt-3 max-w-md text-[#8A7F86]">
            {state === "timeout"
              ? "Your payment is taking a little longer than usual. If you were charged, you'll receive a confirmation email shortly."
              : "It looks like your payment wasn't completed. Your bag is still saved, feel free to try again."}
          </p>
          <Link to="/cart" data-testid="success-retry" className="mt-8 rounded-full border border-[#2C2B2C] rounded-full px-10 py-4 text-xs uppercase tracking-[0.18em] text-[#2C2B2C]">Return to Bag</Link>
        </>
      )}
    </div>
  );
}
